import { dateDuRappel, downloadReminder } from "../lib/share";
import type { RappelAgenda as Rappel } from "../lib/share";
import { useApp } from "../state/AppState";

/**
 * Le rappel à quatre-vingt-dix jours — 25/08/2026.
 *
 * ## Pourquoi il vaut plus qu'il n'en a l'air
 *
 * Le produit ne demande ni nom, ni numéro, ni adresse. Il n'a donc **aucun
 * moyen de rappeler qui que ce soit**, et c'est une décision, pas un oubli :
 * la règle n° 5 dit qu'aucune donnée nominative n'est requise pour obtenir un
 * résultat. La conséquence honnête de cette décision est que le rappel se pose
 * chez la personne, dans son agenda, dans un calendrier que nous ne voyons
 * pas.
 *
 * C'est aussi, tant que la messagerie et le courriel sont coupés, **le seul
 * mécanisme du produit capable de revoir quelqu'un qui n'achète pas** —
 * c'est-à-dire l'immense majorité des visiteurs.
 *
 * ## Pourquoi ce composant existe alors que le fichier existait déjà
 *
 * `downloadReminder` était écrit, documenté, et branché sur **un écran de
 * résultat sur quatre**. Ni le volet 2, ni le volet 3, ni l'écran dirigeant ne
 * l'offraient — ce dernier portant pourtant le livrable à 15 000 F et la mesure
 * qui bouge le plus vite, puisqu'un dirigeant qui confie une tâche à une IA
 * déplace des heures d'équipe, pas seulement les siennes.
 *
 * Le bloc était recopiable en douze lignes, ce qui est exactement la raison
 * pour laquelle il ne l'a jamais été. Il devient un composant.
 *
 * ## Ce qu'il n'annonce pas
 *
 * Ni promesse de progrès, ni objectif chiffré. Ce qu'on écrit dans l'évènement
 * est la mesure du jour et rien d'autre — la personne comparera elle-même. Un
 * rappel qui annoncerait « vous aurez baissé de dix points » serait un faux
 * signal posé à trois mois, dans un endroit où personne ne pourrait le
 * contredire avant l'échéance.
 */

/**
 * Date du rappel, annoncée avant de déposer le fichier.
 *
 * Elle vient de `dateDuRappel`, la même fonction que celle qui date
 * l'évènement : cet écran **annonce** ce que le fichier **contient**, et les
 * calculer séparément permettait de les désaccorder sans que rien ne le dise.
 */
function dansQuatreVingtDixJours(): string {
  return dateDuRappel().toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export interface RappelAgendaProps {
  /** Le titre de l'évènement déposé dans l'agenda. */
  titre: string;
  /** La mesure du jour, telle qu'elle sera relue dans trois mois. */
  mesure: string;
  /** Ce qui change en trois mois, dit avec les mots de cet écran. */
  pourquoi: string;
  /** Où refaire la mesure. L'accueil par défaut ; `/dirigeant` pour l'axe PME. */
  chemin?: string;
  /** L'écran d'origine, pour les compteurs. */
  ecran: string;
  /** Intitulé de l'accroche. Défaut : la durée de validité de la mesure. */
  accroche?: string;
  /**
   * Le bloc est-il le seul retour possible sur cet écran ? — 25/08/2026.
   *
   * Sur un écran de résultat **gratuit**, oui : la personne n'a donné ni
   * numéro, ni adresse, ni compte. C'est le contrat, et il est juste — mais il
   * fait d'elle quelqu'un qu'on ne peut pas rappeler, alors que c'est
   * exactement celle qu'il faudrait revoir : elle a vu son indice, elle n'a pas
   * encore payé.
   *
   * Le bloc était rendu en bas d'un long écran, avec un bouton fantôme, sous un
   * titre qui décrit un geste — « programmez la re-mesure » — plutôt que ce
   * qu'on y gagne. Ici il porte un bouton plein et dit ce que la personne
   * saura dans trois mois.
   *
   * Il reste **après** l'offre, et c'est délibéré : le remonter au-dessus
   * offrirait une sortie gratuite juste avant de demander 3 000 F.
   */
  proeminent?: boolean;
}

export function RappelAgenda({
  titre,
  mesure,
  pourquoi,
  chemin,
  ecran,
  accroche = "Votre mesure vaut trois mois",
  proeminent = false,
}: RappelAgendaProps) {
  const { say } = useApp();

  const poser = () => {
    downloadReminder({ titre, mesure, chemin, ecran } satisfies Rappel);
    /*
      L'annonce passe par `say` et non par un état local : c'est l'annonceur
      `aria-live` de l'application, celui que les quatre écrans partagent. Un
      message posé dans le corps de la page serait vu par qui regarde, et par
      personne d'autre — or le geste qui vient d'avoir lieu est un
      téléchargement, qui ne se voit pas non plus.
    */
    say("Rappel ajouté à votre calendrier");
  };

  return (
    <div className={proeminent ? "rappel rappel-fort" : "rappel"}>
      <div className="k">{accroche}</div>
      <h3>
        {proeminent
          ? "Dans trois mois, vous saurez si vous avez repris du temps"
          : "Programmez la re-mesure maintenant"}
      </h3>
      <p>
        {pourquoi} Un fichier de rappel se télécharge et s'ouvre dans votre application Agenda
        pour le {dansQuatreVingtDixJours()} — aucun numéro, aucune adresse, rien à retenir. Si
        rien ne s'ouvre, notez simplement cette date.
      </p>
      <button
        type="button"
        className={proeminent ? "btn btn-brass" : "btn btn-ghost"}
        onClick={poser}
      >
        Ajouter le rappel à mon agenda
      </button>
    </div>
  );
}
